import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Clock, XCircle, HelpCircle } from "lucide-react";
import { jsPDF } from "jspdf";
import { getFleetOverview } from "../../lib/api";
import { formatUpdated, formatFreshness, staleReason } from "../../lib/freshness";
import { formatSegment } from "../../lib/segment";
import { PageLoader } from "../../components/ui/spinner";
import { Button } from "../../components/ui/button";
import { StaleBadge } from "../../components/ui/stale-badge";

const STATUS_CONFIG = {
  UP_TO_DATE:      { dot: "bg-emerald-500", pill: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Up to Date",      rowBg: "",              icon: CheckCircle2 },
  MANDATORY:       { dot: "bg-red-500",     pill: "bg-red-100 text-red-700 border-red-200",             label: "Mandatory",       rowBg: "bg-red-50/60",  icon: AlertTriangle },
  PENDING:         { dot: "bg-amber-400",   pill: "bg-amber-100 text-amber-700 border-amber-200",       label: "Pending",         rowBg: "",              icon: Clock },
  FAILED_ROLLBACK: { dot: "bg-red-600",     pill: "bg-red-100 text-red-700 border-red-200",             label: "Failed - Rollback", rowBg: "bg-red-50/60", icon: XCircle },
  // Compliance that cannot be derived (no firmware history, or no mandatory build
  // rolling out). Grey, never green: it must not read as verified compliant (DISC-002).
  UNKNOWN:         { dot: "bg-slate-400",   pill: "bg-slate-100 text-slate-500 border-slate-200",       label: "Unknown",         rowBg: "",              icon: HelpCircle },
};

const FILTERS = [
  { key: "ALL",            label: "All" },
  { key: "UP_TO_DATE",     label: "Up to Date" },
  { key: "MANDATORY",      label: "\u26A0 Mandatory" },
  // No Pending / Failed chips: the service derives only Up to Date, Update Required
  // and Unknown, so those two could never match a device (DISC-005).
  { key: "UNKNOWN",        label: "Unknown" },
];

// Filter-chip styling: the MANDATORY chip turns red once selected, the others navy.
function filterChipClass(isActive, isMandatory) {
  if (!isActive) return "bg-white text-slate-600 border-slate-300 hover:border-teal-400";
  if (isMandatory) return "bg-white border-red-400 text-red-600";
  return "bg-[#08148C] border-[#08148C] text-white";
}

// Aging severity: red past 90 days, amber past 30.
function agingDaysClass(days) {
  if (days >= 90) return "text-red-600";
  if (days >= 30) return "text-amber-600";
  return "text-slate-600";
}

// Status chip -> the service's status filter. Search and status are applied by the
// service before pagination (FMP-FL-004), so every page, the counts and the export
// cover the whole fleet, not one downloaded batch.
const STATUS_QUERY = { UP_TO_DATE: "up_to_date", MANDATORY: "update_required", UNKNOWN: "unknown" };
const PAGE_SIZE = 10;
const EXPORT_BATCH = 100;      // the service's MAX_PAGE_LIMIT
const SEARCH_DEBOUNCE_MS = 400;

function formatCountry(value) {
  if (!value) return "-";
  const normalised = String(value).trim();
  if (!normalised) return "-";
  if (normalised.length === 2) {
    return normalised.toUpperCase();
  }
  return normalised
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function addWrappedText(doc, text, x, y, options = {}) {
  const { maxWidth = 170, lineHeight = 6 } = options;
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + (lines.length * lineHeight);
}

export default function DeviceRegistryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedCountryFilter, setAppliedCountryFilter] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  // A dashboard tile deep-links here with ?status=PENDING (etc.) to land the
  // fleet view already filtered instead of dumping the user on "All" (WP-HUB-02).
  const statusParam = searchParams.get("status");
  const initialFilter = FILTERS.some((f) => f.key === statusParam) ? statusParam : "ALL";
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [totalDevices, setTotalDevices] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  // Counts from the service's status_summary for the filtered set (null until loaded, or
  // when the service omits it) - never counted from the rows on this page (DISC-004).
  const [statusSummary, setStatusSummary] = useState(null);
  // Set when the fleet list could not be loaded. The page then says so instead of
  // showing "0 devices" and empty counts, which read as an empty, compliant fleet.
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const requestSeq = useRef(0);
  // Arrived through a hub tile (TC-0004 FT-004): the first fetch carries
  // view=hub_tile so the service records the followed deep link. Consumed once -
  // later filter changes and paging are ordinary fetches.
  const location = useLocation();
  const hubTileRef = useRef(location.state?.via === "hub_tile" ? location.state : null);

  // The search text as sent to the service: settles SEARCH_DEBOUNCE_MS after typing
  // stops, so a word is one request rather than one per key.
  const [appliedSearch, setAppliedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const countryOptions = useMemo(() => {
    const values = new Set();
    devices.forEach((device) => {
      const base = formatCountry(device.baseCountry);
      if (base !== "-") values.add(base);
    });
    if (countryFilter) values.add(countryFilter);
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [devices, countryFilter]);

  const fetchPage = (page, country = appliedCountryFilter, from = appliedFromDate, to = appliedToDate) => {
    setLoading(true);
    const seq = ++requestSeq.current;
    const hubTile = hubTileRef.current;
    hubTileRef.current = null;
    getFleetOverview({
      page,
      limit: PAGE_SIZE,
      country: country.trim(),
      fromDate: from,
      toDate: to,
      ...(hubTile ? { view: "hub_tile", hubTile: String(hubTile.tile || ""), hubFilter: activeFilter } : {}),
      search: appliedSearch,
      status: STATUS_QUERY[activeFilter] || "",
    })
      .then((data) => {
        // An earlier, slower request can resolve after a newer one - ignore it
        // so it can't clobber the current filter's results with a stale page.
        if (seq !== requestSeq.current) return;
        setLoadError(null);
        setDevices(data?.devices || []);
        setTotalDevices(Number(data?.totalDevices ?? data?.devices?.length ?? 0));
        setStatusSummary(data?.statusSummary || null);
        setPagination(data?.pagination || { page, totalPages: 1, total: 0 });
      })
      .catch((err) => {
        console.error(err);
        if (seq !== requestSeq.current) return;
        // Nothing from a failed load is shown as data.
        setLoadError(err?.message || "request failed");
        setDevices([]);
        setStatusSummary(null);
        setTotalDevices(0);
        setPagination({ page: 1, totalPages: 1, total: 0 });
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  };

  // A new status chip or settled search text starts again at page 1.
  useEffect(() => {
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, appliedSearch]);

  // The counts of the filtered set, as the service reports them (search, country and
  // dates applied; not the status chip, so each card is what its chip would list).
  // A count the service did not send is shown as "-", not as 0.
  const count = (v) => (v == null ? "-" : v);
  const upToDate  = count(statusSummary?.updated);
  const mandatory = count(statusSummary?.mandatory);
  const unknown   = count(statusSummary?.unknown);

  // The service returns the filtered page itself.
  const displayedDevices = devices;
  const displayTotalPages = pagination.totalPages;
  const displayCurrentPage = pagination.page;
  const goToPage = (p) => fetchPage(p);

  // The export is every device matching the filters on screen: the same query,
  // paged through the service, so the PDF matches the "N devices" shown above the table.
  const exportQuery = () => ({
    country: appliedCountryFilter.trim(),
    fromDate: appliedFromDate,
    toDate: appliedToDate,
    search: appliedSearch,
    status: STATUS_QUERY[activeFilter] || "",
  });
  const fetchAllDevicesForExport = async () => {
    const all = [];
    let summary = null;
    let page = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const data = await getFleetOverview({ page, limit: EXPORT_BATCH, ...exportQuery() });
      if (page === 1) summary = data?.statusSummary || null;
      const batch = data?.devices || [];
      all.push(...batch);
      const totalPages = data?.pagination?.totalPages || 1;
      if (page >= totalPages || batch.length === 0) break;
      page += 1;
    }
    return { devices: all, summary };
  };

  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await runExportPDF();
    } finally {
      setExporting(false);
    }
  };

  const runExportPDF = async () => {
    const { devices: exportDevices, summary: exportSummary } = await fetchAllDevicesForExport();
    const q = exportQuery();
    const filterText = [
      q.search && `search "${q.search}"`,
      activeFilter !== "ALL" && `status ${FILTERS.find((f) => f.key === activeFilter)?.label.replace("⚠ ", "")}`,
      q.country && `country ${q.country}`,
      q.fromDate && `registered from ${q.fromDate}`,
      q.toDate && `registered to ${q.toDate}`,
    ].filter(Boolean).join(", ") || "none";
    const c = (v) => (v == null ? "-" : v);

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    let y = 18;

    const ensureSpace = (needed = 10) => {
      if (y + needed <= pageHeight - 14) return;
      doc.addPage();
      y = 18;
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Fleet Overview Export", marginX, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, y);
    y += 6;
    doc.text(`Filters: ${filterText}`, marginX, y);
    y += 6;
    doc.text(`Total devices: ${exportDevices.length}   Up to Date: ${c(exportSummary?.updated)}   `
      + `Mandatory Required: ${c(exportSummary?.mandatory)}   Unknown: ${c(exportSummary?.unknown)}`, marginX, y);
    y += 8;

    exportDevices.forEach((device, index) => {
      const updated = formatUpdated(device.lastUpdated);
      ensureSpace(48);

      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(marginX, y - 4, 182, 40, 2, 2);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(`${index + 1}. ${device.serial || "-"}`, marginX + 4, y + 2);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);

      let rowY = y + 8;
      rowY = addWrappedText(doc, `Model: ${device.model || "Unknown"}`, marginX + 4, rowY, { maxWidth: 84 });
      rowY = addWrappedText(doc, `Segment: ${formatSegment(device.segment).text}`, marginX + 4, rowY, { maxWidth: 84 });
      rowY = addWrappedText(doc, `User Name: ${device.name || "-"}`, marginX + 4, rowY, { maxWidth: 84 });
      rowY = addWrappedText(doc, `Current FW: ${device.currentFw || "-"}`, marginX + 4, rowY, { maxWidth: 84 });
      addWrappedText(doc, `Required FW: ${device.requiredFw || "-"}`, marginX + 4, rowY, { maxWidth: 84 });

      let rightY = y + 8;
      const statusLabel = STATUS_CONFIG[device.status]?.label || device.status || "-";
      // A stale status is the last known one and is printed as such (FMP-AN-009).
      const statusText = device.stale ? `STALE (${staleReason(device)}) - last known: ${statusLabel}` : statusLabel;
      rightY = addWrappedText(doc, `Status: ${statusText}`, marginX + 94, rightY, { maxWidth: 84 });
      rightY = addWrappedText(doc, `Last Updated: ${updated.date} ${updated.time} (${formatFreshness(device.lastUpdated)})`, marginX + 94, rightY, { maxWidth: 84 });
      addWrappedText(doc, `Aging Days: ${device.agingDays != null ? device.agingDays + " days" : "-"}`, marginX + 94, rightY, { maxWidth: 84 });

      y += 46;
    });

    doc.save("fleet_overview.pdf");
  };

  if (loading) return <PageLoader />;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fleet Overview</h1>
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          className="flex items-center gap-2 text-sm font-medium border border-slate-300 rounded-lg px-4 py-2 bg-white hover:bg-slate-50 transition-colors text-slate-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          &#8595; {exporting ? "Exporting..." : "Export PDF"}
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard value={upToDate}  label="Up to Date"         color="text-emerald-600" />
        <StatCard value={mandatory} label="Mandatory Required" color="text-orange-600"  />
        <StatCard value={unknown}   label="Unknown"            color="text-slate-500"   />
      </div>

      {/* Search + filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Server Filters</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3 items-end">
          <div>
            <label htmlFor="registry-country-filter" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Country
            </label>
            <select
              id="registry-country-filter"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="h-10 w-full px-3 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Countries</option>
              {countryOptions.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="registry-from-date" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Registered From
            </label>
            <input
              id="registry-from-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              title="Registered from"
              className="h-10 w-full px-3 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label htmlFor="registry-to-date" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Registered To
            </label>
            <input
              id="registry-to-date"
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              title="Registered to"
              className="h-10 w-full px-3 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setAppliedCountryFilter(countryFilter);
                  setAppliedFromDate(fromDate);
                  setAppliedToDate(toDate);
                  fetchPage(1, countryFilter, fromDate, toDate);
                }}
                className="h-10 px-3 text-sm font-semibold rounded-lg border border-[#08148C] bg-[#08148C] text-white hover:bg-[#0d1fa8]"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => {
                  setCountryFilter("");
                  setFromDate("");
                  setToDate("");
                  setAppliedCountryFilter("");
                  setAppliedFromDate("");
                  setAppliedToDate("");
                  fetchPage(1, "", "", "");
                }}
                className="h-10 px-3 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="xl:col-span-2 xl:max-w-md">
            <label htmlFor="registry-search" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                id="registry-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={100}
                placeholder="Serial number, model, user name, country or firmware version..."
                className="h-10 w-full pl-9 pr-4 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">

          <div className="h-5 w-px bg-slate-200 mx-2 hidden sm:block" />

         
          {FILTERS.map((f) => {
            const isMandatory = f.key === "MANDATORY";
            const isActive = activeFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`h-9 px-3.5 rounded-full text-sm font-medium border transition-colors ${filterChipClass(isActive, isMandatory)}`}
              >
                {f.label}
              </button>
            );
          })}

          <span className="ml-auto text-sm font-medium text-slate-400">
            {loadError ? "-" : totalDevices} devices
          </span>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-amber-800 flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold">Fleet status unavailable</p>
            <p className="text-sm mt-1">The device list could not be loaded, so no devices or counts are shown.</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => fetchPage(pagination.page || 1)}>
            Retry
          </Button>
        </div>
      )}

      {/* Table */}
      {!loadError && (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* The 13 columns do not fit a laptop viewport. overflow-hidden on the
            card keeps the rounded corners but clipped STATUS and DETAILS with no
            way to reach them, so the scroll lives here; min-w stops the columns
            squashing into each other instead of scrolling. */}
        <div className="overflow-x-auto">
        <table className="w-full min-w-[1280px] text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              {["SERIAL NO.", "MODEL", "SEGMENT", "USER NAME", "CURRENT COUNTRY", "BASE COUNTRY", "REGISTERED AT", "CURRENT FW", "REQUIRED FW", "LAST UPDATED", "AGING DAYS", "STATUS", "DETAILS"].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedDevices.map((d) => {
              const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.UNKNOWN;
              const seg = formatSegment(d.segment);
              const { date, time } = formatUpdated(d.lastUpdated);
              const registered = formatUpdated(d.registeredAt);
              const hasRequiredFw = d.requiredFw != null && String(d.requiredFw).trim() !== "";
              const isOutdated = hasRequiredFw && d.currentFw !== d.requiredFw;
              return (
                <tr
                  key={d.id}
                  className={`border-b border-slate-100 transition-colors ${cfg.rowBg || "hover:bg-slate-50"}`}
                >
                  {/* Serial */}
                  <td className="px-4 py-3.5 font-bold text-slate-800">{d.serial}</td>

                  {/* Model */}
                  <td className="px-4 py-3.5 text-teal-600 font-medium">{d.model}</td>

                  {/* Segment */}
                  <td className={`px-4 py-3.5 text-xs ${seg.className}`}>{seg.text}</td>

                  {/* User Name */}
                  <td className="px-4 py-3.5 text-slate-600 font-medium">{d.name || "-"}</td>

                  {/* Current country */}
                  <td className="px-4 py-3.5 text-slate-600">{formatCountry(d.currentCountry)}</td>

                  {/* Base country */}
                  <td className="px-4 py-3.5 text-slate-600">{formatCountry(d.baseCountry)}</td>

                  {/* Registered At */}
                  <td className="px-4 py-3.5 text-slate-500 text-xs leading-5">
                    <span className="block">{registered.date}</span>
                    <span className="block">{registered.time}</span>
                  </td>

                  {/* Current FW */}
                  <td className="px-4 py-3.5">
                    <span className={isOutdated ? "text-slate-500" : "text-emerald-600 font-semibold"}>
                      {d.currentFw}
                    </span>
                  </td>

                  {/* Required FW */}
                  <td className="px-4 py-3.5">
                    {!hasRequiredFw && <span className="text-slate-400 font-medium">-</span>}
                    {hasRequiredFw && isOutdated && (
                      <span className="text-orange-600 font-semibold flex items-center gap-1">
                        {d.requiredFw}
                        <span className="text-orange-500">&#x26A0;</span>
                      </span>
                    )}
                    {hasRequiredFw && !isOutdated && (
                      <span className="text-slate-500">{d.requiredFw}</span>
                    )}
                  </td>

                  {/* Last Updated */}
                  <td className="px-4 py-3.5 text-slate-500 text-xs leading-5">
                    <span className="block">{date}</span>
                    <span className="block">{time}</span>
                    <span className="block text-slate-400" data-freshness>{formatFreshness(d.lastUpdated)}</span>
                  </td>

                  {/* Aging Days */}
                  <td className="px-4 py-3.5 text-center">
                    {d.agingDays != null ? (
                      <span className={`text-sm font-semibold ${agingDaysClass(d.agingDays)}`}>
                        {d.agingDays}d
                      </span> 
                    ) : (
                      <span className="text-slate-400 text-xs">-</span>
                    )}
                  </td>

                  {/* Status - a stale record is flagged in its place, never shown as current (FMP-AN-009) */}
                  <td className="px-4 py-3.5">
                    {d.stale ? (
                      <StaleBadge device={d} lastKnown={cfg.label} />
                    ) : (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${cfg.pill}`}>
                        <cfg.icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                        {cfg.label}
                      </span>
                    )}

                  </td>

                  {/* Details */}
                  <td className="px-4 py-3.5">
                    <button
                      // WP-FL-04: a Failed/Rollback device routes to its Audit view,
                      // not the standard detail (TC-0031). View is unchanged.
                      onClick={() => navigate(d.status === "FAILED_ROLLBACK"
                        ? `/devices/${d.serial}?tab=audit`
                        : `/devices/${d.serial}`)}
                      className="text-xs font-medium border border-slate-300 rounded-md px-3 py-1.5 text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      {d.status === "FAILED_ROLLBACK" ? "Audit" : "View"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      {displayTotalPages > 1 && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || displayCurrentPage <= 1}
              onClick={() => goToPage(displayCurrentPage - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            {Array.from({ length: displayTotalPages }, (_, i) => i + 1).map((p) => {
              const current = displayCurrentPage;
              const total = displayTotalPages;
              const show = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter  = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) {
                return <span key={p} className="px-1 text-sm text-slate-400">...</span>;
              }
              if (!show) return null;
              return (
                <Button
                  key={p}
                  type="button"
                  variant={p === current ? "default" : "outline"}
                  size="sm"
                  disabled={loading}
                  onClick={() => goToPage(p)}
                  className="min-w-9"
                >
                  {p}
                </Button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || displayCurrentPage >= displayTotalPages}
              onClick={() => goToPage(displayCurrentPage + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ value, label, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}